import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from 'react-query'
import useSmartPolling from './useSmartPolling'
import { chatConfig } from '../../../config/chatConfig'
import { showChatErrorNotification } from './errorHandling'
import {
  getAllConversations,
  getConversationById,
  createConversation,
  updateConversation,
  deleteConversation,
  addParticipant,
  removeParticipant,
  getConversationMessages,
  getMessages,
  sendMessage,
  getMessageById,
  updateMessage,
  deleteMessage,
  markMessageAsRead,
  markMessageAsDelivered,
  addReaction,
  removeReaction,
  getConversationMedia,
  uploadMedia,
  getMediaById,
  deleteMedia,
  getAllTenants,
  getTenantById,
  createTenant,
  updateTenant,
  deleteTenant,
  getUserProfile,
  testHrApiConnectivity,
  testAuth,
  getCurrentUser,
  pinMessage,
  unpinMessage,
  pinConversation,
  unpinConversation,
  forwardMessages,
  getConversationsList,
  getMediaSignedUrl,
  demoteAdmin,
  makeAdmin,
  getUnreadCount,
  markReadUpTo,
  getMessageReaders,
} from './chatApiService'

// =============================================
// CACHE KEY HELPERS
// =============================================

/**
 * Standard cache key builder for messages
 * Ensures consistency across all message-related queries and mutations
 * 
 * @param {string} conversationId - The conversation UUID
 * @param {number} limit - Number of messages per page (default: 50)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Array} Cache key array for React Query
 */
export const getMessagesCacheKey = (conversationId, limit = 50, offset = 0) => {
  return ['messages', conversationId, limit, offset]
}

/**
 * Standard cache key builder for conversations
 * Ensures consistency across all conversation-related queries and mutations
 * 
 * @param {number} limit - Number of conversations per page (default: 20)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Array} Cache key array for React Query
 */
export const getConversationsCacheKey = (limit = 20, offset = 0) => {
  return ['conversations', limit, offset]
}

export const useMessageReaders = (conversationId, messageUuid) => {
  return useQuery(
    ['messageReaders', conversationId, messageUuid],
    async () => {
      // Import cache dynamically to avoid circular dependencies
      const { default: readReceiptsCache } = await import('./readReceiptsCache')

      // Try to get from cache first (WebSocket data)
      const cached = readReceiptsCache.getReaders(conversationId, messageUuid)

      if (cached.cached) {
        console.log('✅ Read status from cache (no API call):', {
          messageId: messageUuid?.substring(0, 8) + '...',
          readers: cached.count,
        })
        return cached
      }

      // Fallback to API if not in cache (initial load or cache miss)
      console.log('📡 Fetching read status from API (cache miss):', {
        messageId: messageUuid?.substring(0, 8) + '...',
      })
      const apiData = await getMessageReaders(conversationId, messageUuid)

      // Store API response in cache for future use
      if (apiData && apiData.readers) {
        apiData.readers.forEach((userId) => {
          readReceiptsCache.addReadReceipt(conversationId, messageUuid, userId)
        })
      }

      return apiData
    },
    {
      enabled: !!conversationId && !!messageUuid,
      staleTime: 30000,          // Cache for 30 seconds (increased from 5s)
      cacheTime: 300000,         // Keep in memory for 5 minutes (increased from 60s)
      refetchOnWindowFocus: false,
      refetchOnMount: false,     // Don't refetch on mount (use cache)
      refetchOnReconnect: false, // Don't refetch on reconnect (use cache)
    }
  )
}

/**
 * Custom hook to fetch and cache link previews
 * Prevents refetching on breakpoint changes or component remounts
 * Cache persists for 24 hours
 * 
 * @param {string} url - The URL to fetch preview for
 * @param {object} options - Optional React Query options
 * @returns {UseQueryResult} React Query result with preview data
 * 
 * @example
 * const { data: preview, isLoading } = useLinkPreview('https://youtube.com/watch?v=...')
 */
export const useLinkPreview = (url, options = {}) => {
  return useQuery(
    ['linkPreview', url], // Unique cache key per URL
    async () => {
      // Dynamic import to avoid circular dependencies
      const { fetchLinkPreview } = await import('utils/linkPreviewUtils')
      return fetchLinkPreview(url)
    },
    {
      staleTime: 24 * 60 * 60 * 1000, // 24 hours - link previews rarely change
      cacheTime: 48 * 60 * 60 * 1000, // Keep in cache for 48 hours
      retry: 2, // Retry twice on failure
      retryDelay: 1000, // 1 second between retries
      refetchOnMount: false, // ✅ Don't refetch on component mount
      refetchOnWindowFocus: false, // ✅ Don't refetch when window regains focus
      refetchOnReconnect: false, // ✅ Don't refetch on network reconnect
      enabled: !!url, // Only fetch if URL exists
      onError: (error) => {
        console.error('Link preview fetch failed:', url, error)
      },
      ...options, // Allow overriding defaults
    }
  )
}

export const useCurrentUser = () => {
  return useQuery(['currentUser'], getCurrentUser, {
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    // Retry strategy: Don't retry on auth errors
    retry: (failureCount, error) => {
      // Don't retry on 401 (auth errors) - user needs to login
      if (error?.response?.status === 401 || error?.status === 401 || error?.isAuthError) {
        return false
      }
      // Retry up to 2 times for other errors
      return failureCount < 2
    },
    // Error handler
    onError: (error) => {
      console.error('❌ Failed to fetch current user:', error)
      if (error?.response?.status === 401 || error?.status === 401 || error?.isAuthError) {
        console.log('🔒 Authentication required - will redirect to login')
      }
    },
  })
}

// =============================================
// CONVERSATION HOOKS
// =============================================

export const useConversations = (limit = 20, offset = 0) => {
  return useQuery(getConversationsCacheKey(limit, offset), () => getAllConversations(limit, offset), {
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    // Retry strategy: Don't retry on auth errors
    retry: (failureCount, error) => {
      // Don't retry on 401 - user needs to login
      if (error?.response?.status === 401 || error?.status === 401 || error?.isAuthError) {
        return false
      }
      // Retry up to 2 times for other errors
      return failureCount < 2
    },
    // Error handler
    onError: (error) => {
      console.error('❌ Failed to fetch conversations:', error)
      if (error?.response?.status === 401 || error?.status === 401 || error?.isAuthError) {
        console.log('🔒 Authentication required - will redirect to login')
      }
    },
  })
}

// =============================================
// TOTAL UNREAD COUNT HOOK (for header badge)
// =============================================

/**
 * useTotalUnreadCount - Calculate total unread count across all conversations
 * 
 * Performance optimizations:
 * - Reads from React Query cache (no additional API calls)
 * - Uses computed selector pattern
 * - Updates REACTIVELY when conversations cache updates (no polling)
 * - WebSocket events invalidate conversations cache, triggering recalculation
 * 
 * @returns {number} Total unread message count
 */
export const useTotalUnreadCount = () => {
  const queryClient = useQueryClient()

  // Use a query to create a computed value from conversations cache
  return useQuery(
    ['totalUnreadCount'],
    () => {
      // Get conversations from cache (no API call!)
      const conversationsCache = queryClient.getQueryData(getConversationsCacheKey())

      if (!conversationsCache || !conversationsCache.data) {
        return 0
      }

      // Sum up unread_count from all conversations
      const total = conversationsCache.data.reduce((sum, conversation) => {
        return sum + (conversation.unread_count || 0)
      }, 0)

      console.log('📊 Total unread count calculated:', {
        total,
        conversationCount: conversationsCache.data.length,
      })

      return total
    },
    {
      // ✅ REACTIVE: Only recalculate when invalidated by WebSocket events
      // This is triggered by WebSocket events that call:
      // queryClient.invalidateQueries(['totalUnreadCount'])
      enabled: true,
      staleTime: 0,               // Allow refetch when invalidated
      cacheTime: 300000,          // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,      // Don't refetch on mount
      refetchOnReconnect: false,  // Don't refetch on reconnect
      retry: false,
      placeholderData: 0,
      // ❌ REMOVED: refetchInterval - No more polling!
      // Updates are driven by WebSocket events invalidating this query
    }
  )
}

export const useConversation = (conversationId) => {
  return useQuery(['conversation', conversationId], () => getConversationById(conversationId), {
    enabled: !!conversationId,
    staleTime: 30000,
    cacheTime: 300000,
    // Retry strategy
    retry: (failureCount, error) => {
      if (error?.response?.status === 401 || error?.status === 401 || error?.isAuthError) {
        return false
      }
      return failureCount < 2
    },
  })
}

export const useCreateConversation = () => {
  const queryClient = useQueryClient()

  return useMutation(createConversation, {
    onMutate: async (newConversationData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['conversations'])

      // Snapshot the previous value
      const previousConversations = queryClient.getQueryData(['conversations'])

      // Optimistically add the new conversation to the cache
      queryClient.setQueryData(['conversations'], (oldData) => {
        if (!oldData?.data) return oldData

        const optimisticConversation = {
          id: `temp-${Date.now()}`,
          conversation_id: `temp-${Date.now()}`,
          conversation_uuid: `temp-${Date.now()}`,
          title:
            newConversationData.title ||
            `Chat with ${newConversationData.participants?.length || 0} people`,
          type: newConversationData.type || 'personnel',
          participants: newConversationData.participants || [],
          created_by: newConversationData.created_by,
          tenant_uuid: newConversationData.tenant_uuid,
          is_group: newConversationData.is_group || false,
          last_message: '',
          last_message_content: '',
          last_message_sender: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_deleted: false,
          metadata: newConversationData.metadata || {},
        }

        return {
          ...oldData,
          data: [optimisticConversation, ...oldData.data],
          total: oldData.total + 1,
        }
      })

      // Return a context object with the snapshotted value
      return { previousConversations }
    },
    onError: (err, newConversationData, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(['conversations'], context.previousConversations)
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(['conversations'])
    },
  })
}

export const useUpdateConversation = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ conversationId, updateData }) => updateConversation(conversationId, updateData),
    {
      onSuccess: (data, { conversationId }) => {
        queryClient.invalidateQueries(['conversation', conversationId])
        queryClient.invalidateQueries(['conversations'])
      },
    }
  )
}

export const useDeleteConversation = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteConversation, {
    onSuccess: () => {
      queryClient.invalidateQueries(['conversations'])
    },
  })
}

export const useAddParticipant = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ conversationId, participantId }) => addParticipant(conversationId, participantId),
    {
      onMutate: async ({ conversationId, participantId }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['conversation', conversationId])
        await queryClient.cancelQueries(['conversations'])

        // Snapshot the previous values
        const previousConversation = queryClient.getQueryData(['conversation', conversationId])
        const previousConversations = queryClient.getQueryData(['conversations'])

        // Return context with snapshots
        return { previousConversation, previousConversations }
      },
      onSuccess: (data, { conversationId }) => {
        console.log('Add participant SUCCESS:', data)
        // Invalidate and refetch the affected queries
        queryClient.invalidateQueries(['conversation', conversationId])
        queryClient.invalidateQueries(['conversations'])
      },
      onError: (error, { conversationId }, context) => {
        console.error('Error adding participant:', error)

        // Rollback on error
        if (context?.previousConversation) {
          queryClient.setQueryData(['conversation', conversationId], context.previousConversation)
        }
        if (context?.previousConversations) {
          queryClient.setQueryData(['conversations'], context.previousConversations)
        }
      },
      onSettled: (data, error, { conversationId }) => {
        // Always refetch after mutation
        queryClient.invalidateQueries(['conversation', conversationId])
        queryClient.invalidateQueries(['conversations'])
      },
    }
  )
}

export const useRemoveParticipant = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ conversationId, participantId }) => removeParticipant(conversationId, participantId),
    {
      onSuccess: (data, { conversationId }) => {
        queryClient.invalidateQueries(['conversation', conversationId])
        queryClient.invalidateQueries(['conversations'])
      },
    }
  )
}

// Hook for making a user an admin
export const useMakeAdmin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, targetUuid }) => makeAdmin(conversationId, targetUuid),
    onSuccess: (data, { conversationId }) => {
      console.log('Make admin API SUCCESS:', data)
      queryClient.invalidateQueries(['conversation', conversationId])
      queryClient.invalidateQueries(['conversations'])
    },
    onError: (error) => {
      console.error('Failed to make user admin:', error)
      throw error
    },
  })
}

// Hook for demoting an admin
export const useDemoteAdmin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, targetUuid }) => demoteAdmin(conversationId, targetUuid),
    onSuccess: (data, { conversationId }) => {
      console.log('Demote admin API SUCCESS:', data)
      queryClient.invalidateQueries(['conversation', conversationId])
      queryClient.invalidateQueries(['conversations'])
    },
    onError: (error) => {
      console.error('Failed to demote admin:', error)
      throw error
    },
  })
}

// Hook for pinning a conversation
export const usePinConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: pinConversation,
    onMutate: async (conversationId) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(['conversations'])

      // Snapshot the previous value
      const previousConversations = queryClient.getQueryData(['conversations'])

      // Optimistically update to the new value
      queryClient.setQueryData(['conversations'], (oldData) => {
        if (!oldData?.data) return oldData

        return {
          ...oldData,
          data: oldData.data.map((conversation) => {
            if (
              (conversation.conversation_uuid || conversation.uuid || conversation.id) ===
              conversationId
            ) {
              console.log('OPTIMISTIC UPDATE - PINNING:', {
                conversationId,
                before: conversation.is_pinned,
                after: true,
              })
              return {
                ...conversation,
                is_pinned: true,
                updated_at: new Date().toISOString(),
              }
            }
            return conversation
          }),
        }
      })

      // Return a context object with the snapshotted value
      return { previousConversations }
    },
    onSuccess: (data, conversationId) => {
      console.log('Pin conversation API SUCCESS:', data)

      // Force invalidate and refetch to get the latest data from server
      queryClient.invalidateQueries(['conversations'])
      queryClient.invalidateQueries(['conversation', conversationId])
    },
    onError: (error, conversationId, context) => {
      console.error('Error pinning conversation:', error)

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(['conversations'], context.previousConversations)
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries(['conversations'])
    },
  })
}

// Hook for unpinning a conversation
export const useUnpinConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: unpinConversation,
    onMutate: async (conversationId) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(['conversations'])

      // Snapshot the previous value
      const previousConversations = queryClient.getQueryData(['conversations'])

      // Optimistically update to the new value
      queryClient.setQueryData(['conversations'], (oldData) => {
        if (!oldData?.data) return oldData

        return {
          ...oldData,
          data: oldData.data.map((conversation) => {
            if (
              (conversation.conversation_uuid || conversation.uuid || conversation.id) ===
              conversationId
            ) {
              console.log('OPTIMISTIC UPDATE - UNPINNING:', {
                conversationId,
                before: conversation.is_pinned,
                after: false,
              })
              return {
                ...conversation,
                is_pinned: false,
                updated_at: new Date().toISOString(),
              }
            }
            return conversation
          }),
        }
      })

      // Return a context object with the snapshotted value
      return { previousConversations }
    },
    onSuccess: (data, conversationId) => {
      console.log('Unpin conversation API SUCCESS:', data)

      // Force invalidate and refetch to get the latest data from server
      queryClient.invalidateQueries(['conversations'])
      queryClient.invalidateQueries(['conversation', conversationId])
    },
    onError: (error, conversationId, context) => {
      console.error('Error unpinning conversation:', error)

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(['conversations'], context.previousConversations)
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries(['conversations'])
    },
  })
}

export const useUnreadCount = (conversationId) => {
  return useSmartPolling(
    ['unreadCount', conversationId],
    () => getUnreadCount(conversationId),
    {
      enabled: !!conversationId,
      staleTime: chatConfig.cache.staleTime,
      refetchInterval: chatConfig.pollingIntervals.unreadCount,
      onError: (error) => {
        showChatErrorNotification(error)
      },
    }
  )
}

// Hook to mark messages as read
export const useMarkReadUpTo = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ conversationId, messageUuid }) => markReadUpTo(conversationId, messageUuid),
    {
      onSuccess: (data, { conversationId }) => {
        // Invalidate unread count
        queryClient.invalidateQueries(['unreadCount', conversationId])
        // Invalidate conversations to update unread badges
        queryClient.invalidateQueries(['conversations'])
        // Invalidate the specific conversation
        queryClient.invalidateQueries(['conversation', conversationId])
      },
    }
  )
}

// =============================================
// MESSAGE HOOKS
// =============================================

export const useMessages = (conversationUuid, limit = 50, offset = 0) => {
  return useSmartPolling(
    ['messages', conversationUuid, limit, offset],
    () => getMessages(conversationUuid, limit, offset),
    {
      enabled: !!conversationUuid,
      staleTime: chatConfig.cache.staleTime,
      cacheTime: chatConfig.cache.cacheTime,
      refetchInterval: chatConfig.pollingIntervals.messages,
      onError: (error) => {
        showChatErrorNotification(error)
      },
    }
  )
}

export const useInfiniteMessages = (conversationUuid, limit = 50) => {
  return useInfiniteQuery(
    ['messages', conversationUuid],
    ({ pageParam = 0 }) => {
      console.log('🔍 Fetching messages with offset:', pageParam)
      return getMessages(conversationUuid, limit, pageParam)
    },
    {
      enabled: !!conversationUuid,
      getNextPageParam: (lastPage, allPages) => {
        // Handle different API response structures
        let messagesInPage = []
        
        if (Array.isArray(lastPage)) {
          messagesInPage = lastPage
        } else if (lastPage.data && Array.isArray(lastPage.data)) {
          messagesInPage = lastPage.data
        } else if (lastPage.messages && Array.isArray(lastPage.messages)) {
          messagesInPage = lastPage.messages
        } else if (lastPage.items && Array.isArray(lastPage.items)) {
          messagesInPage = lastPage.items
        }
        
        console.log('📊 Page stats:', {
          messagesInPage: messagesInPage.length,
          limit,
          totalPagesLoaded: allPages.length,
          currentOffset: allPages.length * limit,
        })
        
        // If we got 0 messages or significantly fewer (< 50% of limit), we've reached the end
        // Being less strict here because sometimes the API might return 19 instead of 20
        if (messagesInPage.length === 0 || messagesInPage.length < Math.floor(limit * 0.5)) {
          console.log('✅ Reached end of messages (got', messagesInPage.length, 'of', limit, ')')
          return undefined
        }
        
        // Calculate next offset based on total pages loaded
        const nextOffset = allPages.length * limit
        console.log('➡️ Next offset will be:', nextOffset, '(will fetch', limit, 'messages)')
        return nextOffset
      },
      staleTime: 10000,
      cacheTime: 300000,
    }
  )
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()

  return useMutation(
    // The actual API call - clean payload without sender info (backend sets it from auth)
    (messageData) => {
      const apiPayload = {
        conversation_uuid: messageData.conversation_uuid,
        content: messageData.content,
        type: messageData.type,
        ...(messageData.media && { media: messageData.media }),
        ...(messageData.reply_to && { reply_to: messageData.reply_to }),
        ...(messageData.mentioned_users && { mentioned_users: messageData.mentioned_users }),
      }
      return sendMessage(apiPayload)
    },
    {
      // Optimistic update - add message to UI immediately
      onMutate: async (newMessage) => {
        const conversationId = newMessage.conversation_uuid

        // ============================================
        // Use consistent cache keys
        // ============================================
        const messagesCacheKey = getMessagesCacheKey(conversationId)
        const conversationsCacheKey = getConversationsCacheKey()

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(messagesCacheKey)
        await queryClient.cancelQueries(conversationsCacheKey)

        // Snapshot the previous values
        const previousMessages = queryClient.getQueryData(messagesCacheKey)
        const previousConversations = queryClient.getQueryData(conversationsCacheKey)

        // Create optimistic message (includes sender info for display)
        const optimisticMessage = {
          ...newMessage,
          // Temporary ID (will be replaced with real one from API)
          id: `temp-${Date.now()}-${Math.random()}`,
          message_id: `temp-${Date.now()}-${Math.random()}`,
          uuid: `temp-${Date.now()}-${Math.random()}`,
          createdAt: new Date().toISOString(),
          created_at: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          is_sending: true, // Flag to show sending state
          reactions: [],
          is_edited: false,
          is_deleted: false,
        }

        // ✨ OPTIMIZATION 1: Optimistically update messages cache
        queryClient.setQueryData(messagesCacheKey, (old) => {
          // Handle different response structures from the API
          if (Array.isArray(old)) {
            // Direct array
            return [...old, optimisticMessage]
          } else if (old?.data && Array.isArray(old.data)) {
            // Wrapped in data object
            return {
              ...old,
              data: [...old.data, optimisticMessage],
            }
          } else if (old?.messages && Array.isArray(old.messages)) {
            // Wrapped in messages object
            return {
              ...old,
              messages: [...old.messages, optimisticMessage],
            }
          }

          // Fallback: create new structure
          return {
            data: [optimisticMessage],
          }
        })

        // ✨ OPTIMIZATION 2: Optimistically update conversations list
        queryClient.setQueryData(conversationsCacheKey, (oldConversations) => {
          if (!oldConversations?.data) return oldConversations

          console.log('📝 Optimistically updating conversations list for sent message')

          // Helper function to generate preview text based on message type
          const getMessagePreview = (message) => {
            // Check if it's a media message
            if (message.type === 'file' && message.media && message.media.length > 0) {
              const mediaCount = message.media.length
              // Return appropriate preview based on media count
              if (mediaCount === 1) {
                return 'Sent an attachment'
              } else {
                return `Sent ${mediaCount} attachments`
              }
            }
            // For text messages, return the content
            return message.content || ''
          }

          const messagePreview = getMessagePreview(newMessage)

          return {
            ...oldConversations,
            data: oldConversations.data.map((conv) => {
              const convId = conv.conversation_uuid || conv.uuid || conv.id
              if (convId === conversationId) {
                return {
                  ...conv,
                  last_message: messagePreview,
                  last_message_content: messagePreview,
                  last_message_type: newMessage.type,
                  last_message_sender_uuid: newMessage.sender_uuid,
                  last_message_sender: newMessage.sender_name,
                  last_message_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  sort_ts: new Date().toISOString(),
                  // Don't change unread_count for own messages
                }
              }
              return conv
            }),
          }
        })

        // ✨ Trigger totalUnreadCount recalculation (it reads from conversations cache)
        queryClient.invalidateQueries(['totalUnreadCount'])

        // Return context with previous data for rollback
        return {
          previousMessages,
          previousConversations,
          optimisticMessage,
          conversationId,
          messagesCacheKey,
          conversationsCacheKey
        }
      },

      // On success, replace optimistic message with real one
      onSuccess: (realMessage, variables, context) => {
        if (!context?.optimisticMessage) {
          // No optimistic update was made, just invalidate with consistent keys
          queryClient.invalidateQueries(context?.messagesCacheKey || getMessagesCacheKey(variables.conversation_uuid))
          queryClient.invalidateQueries(getConversationsCacheKey())
          return
        }

        const mergedMessage = {
          ...context.optimisticMessage,
          ...realMessage,
          is_sending: false,
          sender_uuid: realMessage.sender_uuid || context.optimisticMessage.sender_uuid,
          sender_name: realMessage.sender_name || context.optimisticMessage.sender_name,
          sender_avatar:
            realMessage.sender_avatar !== undefined
              ? realMessage.sender_avatar
              : context.optimisticMessage.sender_avatar,
        }

        // Update the message in cache with real data
        queryClient.setQueryData(context.messagesCacheKey, (old) => {
          if (Array.isArray(old)) {
            return old.map((msg) =>
              msg.id === context.optimisticMessage.id ? mergedMessage : msg
            )
          }

          if (old?.data && Array.isArray(old.data)) {
            return {
              ...old,
              data: old.data.map((msg) =>
                msg.id === context.optimisticMessage.id ? mergedMessage : msg
              ),
            }
          }

          if (old?.messages && Array.isArray(old.messages)) {
            return {
              ...old,
              messages: old.messages.map((msg) =>
                msg.id === context.optimisticMessage.id ? mergedMessage : msg
              ),
            }
          }

          return old
        })

        // Let WebSocket handle the final sync (no need to invalidate immediately)
        // The optimistic update already made it feel instant
      },

      // On error, rollback to previous data
      onError: (err, newMessage, context) => {
        // Rollback both messages and conversations caches
        if (context?.previousMessages && context?.messagesCacheKey) {
          queryClient.setQueryData(context.messagesCacheKey, context.previousMessages)
        }

        if (context?.previousConversations && context?.conversationsCacheKey) {
          queryClient.setQueryData(context.conversationsCacheKey, context.previousConversations)
        }

        // Also invalidate totalUnreadCount to recalculate
        queryClient.invalidateQueries(['totalUnreadCount'])

        console.error('Failed to send message:', err)
      },

      // No onSettled invalidation required; WebSocket + optimistic updates keep cache fresh
    })
}

export const useMessage = (messageId) => {
  return useQuery(['message', messageId], () => getMessageById(messageId), {
    enabled: !!messageId,
    staleTime: 30000,
    cacheTime: 300000,
  })
}

export const useUpdateMessage = () => {
  const queryClient = useQueryClient()

  return useMutation(({ messageId, updateData }) => updateMessage(messageId, updateData), {
    onSuccess: (data, { messageId }) => {
      queryClient.invalidateQueries(['message', messageId])
      // Invalidate messages for the conversation this message belongs to
      if (data.conversation_uuid) {
        queryClient.invalidateQueries(getMessagesCacheKey(data.conversation_uuid))
      }
    },
  })
}

export const useDeleteMessage = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteMessage, {
    onSuccess: (data, messageId) => {
      queryClient.invalidateQueries(['message', messageId])
      // Invalidate messages for the conversation this message belonged to
      if (data.conversation_uuid) {
        queryClient.invalidateQueries(getMessagesCacheKey(data.conversation_uuid))
      }
    },
  })
}

export const usePinMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, messageUuid }) => pinMessage(conversationId, messageUuid),
    onSuccess: (data, { conversationId }) => {
      console.log('Pin message API SUCCESS:', data)
      // Invalidate conversation data to get updated pinned message info
      queryClient.invalidateQueries(['conversation', conversationId])
      // Optionally invalidate messages if needed
      queryClient.invalidateQueries(getMessagesCacheKey(conversationId))
      // Invalidate conversations list to update any pinned message indicators
      queryClient.invalidateQueries(['conversations'])
    },
    onError: (error, { conversationId, messageUuid }) => {
      console.error('Failed to pin message:', error)
      throw error
    },
  })
}

// NEW: Hook for unpinning a message
export const useUnpinMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, messageUuid }) => unpinMessage(conversationId, messageUuid),
    onSuccess: (data, { conversationId }) => {
      console.log('Unpin message API SUCCESS:', data)
      // Invalidate conversation data to get updated pinned message info
      queryClient.invalidateQueries(['conversation', conversationId])
      // Optionally invalidate messages if needed
      queryClient.invalidateQueries(getMessagesCacheKey(conversationId))
      // Invalidate conversations list to update any pinned message indicators
      queryClient.invalidateQueries(['conversations'])
    },
    onError: (error, { conversationId, messageUuid }) => {
      console.error('Failed to unpin message:', error)
      throw error
    },
  })
}

export const useMarkMessageAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation(markMessageAsRead, {
    onSuccess: (data, messageId) => {
      queryClient.invalidateQueries(['message', messageId])
      if (data.conversation_uuid) {
        queryClient.invalidateQueries(getMessagesCacheKey(data.conversation_uuid))
      }
    },
  })
}

export const useMarkMessageAsDelivered = () => {
  const queryClient = useQueryClient()

  return useMutation(markMessageAsDelivered, {
    onSuccess: (data, messageId) => {
      queryClient.invalidateQueries(['message', messageId])
      if (data.conversation_uuid) {
        queryClient.invalidateQueries(getMessagesCacheKey(data.conversation_uuid))
      }
    },
  })
}

export const useAddReaction = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ messageId, reaction, currentUserId, conversationId }) =>
      addReaction(messageId, reaction),
    {
      // Optimistic update: Update UI immediately before API call completes
      onMutate: async ({ messageId, reaction, currentUserId, conversationId }) => {
        // ✅ Use consistent cache key
        const cacheKey = getMessagesCacheKey(conversationId)

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(cacheKey)

        // Snapshot the previous messages
        const previousMessages = queryClient.getQueryData(cacheKey)

        // Optimistically update the message with the new reaction
        queryClient.setQueryData(cacheKey, (oldData) => {
          if (!oldData?.data) return oldData

          return {
            ...oldData,
            data: oldData.data.map((msg) => {
              if (msg.id === messageId || msg.uuid === messageId || msg.message_id === messageId) {
                // Create updated reactions object
                const updatedReactions = { ...(msg.reactions || {}) }

                // Add current user to the reaction
                if (updatedReactions[reaction]) {
                  // Reaction exists, add user if not already there
                  if (!updatedReactions[reaction].includes(currentUserId)) {
                    updatedReactions[reaction] = [...updatedReactions[reaction], currentUserId]
                  }
                } else {
                  // New reaction
                  updatedReactions[reaction] = [currentUserId]
                }

                return {
                  ...msg,
                  reactions: updatedReactions,
                  reaction_count: Object.values(updatedReactions).flat().length
                }
              }
              return msg
            })
          }
        })

        // Return context with snapshot
        return { previousMessages, cacheKey }
      },
      onSuccess: (data, { messageId, conversationId }) => {
        // ✅ Use consistent cache key
        const cacheKey = getMessagesCacheKey(conversationId)

        // Refetch to get the actual server state
        queryClient.invalidateQueries(['message', messageId])
        queryClient.invalidateQueries(cacheKey)
      },
      onError: (err, variables, context) => {
        // If mutation fails, rollback to previous state
        console.error('Failed to add reaction:', err)
        if (context?.previousMessages && context?.cacheKey) {
          queryClient.setQueryData(context.cacheKey, context.previousMessages)
        }
      },
    }
  )
}

export const useRemoveReaction = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ messageId, reaction, currentUserId, conversationId }) =>
      removeReaction(messageId, reaction),
    {
      // Optimistic update: Update UI immediately before API call completes
      onMutate: async ({ messageId, reaction, currentUserId, conversationId }) => {
        // ✅ Use consistent cache key
        const cacheKey = getMessagesCacheKey(conversationId)

        // Cancel any outgoing refetches
        await queryClient.cancelQueries(cacheKey)

        // Snapshot the previous messages
        const previousMessages = queryClient.getQueryData(cacheKey)

        // Optimistically remove the reaction
        queryClient.setQueryData(cacheKey, (oldData) => {
          if (!oldData?.data) return oldData

          return {
            ...oldData,
            data: oldData.data.map((msg) => {
              if (msg.id === messageId || msg.uuid === messageId || msg.message_id === messageId) {
                // Create updated reactions object
                const updatedReactions = { ...(msg.reactions || {}) }

                // Remove current user from the reaction
                if (updatedReactions[reaction]) {
                  updatedReactions[reaction] = updatedReactions[reaction].filter(
                    (id) => String(id) !== String(currentUserId)
                  )

                  // Remove the reaction entirely if no users left
                  if (updatedReactions[reaction].length === 0) {
                    delete updatedReactions[reaction]
                  }
                }

                return {
                  ...msg,
                  reactions: updatedReactions,
                  reaction_count: Object.values(updatedReactions).flat().length
                }
              }
              return msg
            })
          }
        })

        // Return context with snapshot
        return { previousMessages, cacheKey }
      },
      onSuccess: (data, { messageId, conversationId }) => {
        // ✅ Use consistent cache key
        const cacheKey = getMessagesCacheKey(conversationId)

        // Refetch to get the actual server state
        queryClient.invalidateQueries(['message', messageId])
        queryClient.invalidateQueries(cacheKey)
      },
      onError: (err, variables, context) => {
        // If mutation fails, rollback to previous state
        console.error('Failed to remove reaction:', err)
        if (context?.previousMessages && context?.cacheKey) {
          queryClient.setQueryData(context.cacheKey, context.previousMessages)
        }
      },
    }
  )
}

export const useConversationsList = (limit = 100, offset = 0) => {
  return useQuery(['conversationsList', limit, offset], () => getConversationsList(limit, offset), {
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

// Hook for forwarding messages
export const useForwardMessages = () => {
  const queryClient = useQueryClient()

  return useMutation(forwardMessages, {
    onSuccess: (data, variables) => {
      console.log('Messages forwarded successfully:', data)

      // Invalidate and refetch conversations that received forwarded messages
      variables.to_conversation_uuids.forEach((conversationId) => {
        queryClient.invalidateQueries(getMessagesCacheKey(conversationId))
        queryClient.invalidateQueries(['conversation', conversationId])
      })

      // Also invalidate conversations list to update last message
      queryClient.invalidateQueries(['conversations'])
    },
    onError: (error) => {
      console.error('Error forwarding messages:', error)
    },
  })
}

// =============================================
// MEDIA HOOKS
// =============================================

export const useConversationMedia = (conversationUuid, limit = 50, offset = 0) => {
  return useQuery(
    ['media', conversationUuid, limit, offset],
    () => getConversationMedia(conversationUuid, limit, offset),
    {
      enabled: !!conversationUuid,
      staleTime: 60000, // 1 minute for media
      cacheTime: 600000, // 10 minutes
    }
  )
}

export const useUploadMedia = () => {
  const queryClient = useQueryClient()

  return useMutation(uploadMedia, {
    onSuccess: (data, variables) => {
      const { conversation_uuid } = variables
      if (conversation_uuid) {
        queryClient.invalidateQueries(['media', conversation_uuid])
      }
      queryClient.invalidateQueries(getMessagesCacheKey(conversation_uuid))
    },
  })
}

export const useMedia = (mediaId) => {
  return useQuery(['media', mediaId], () => getMediaById(mediaId), {
    enabled: !!mediaId,
    staleTime: 300000, // 5 minutes for media files
    cacheTime: 1800000, // 30 minutes
  })
}

export const useDeleteMedia = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteMedia, {
    onSuccess: (data, mediaId) => {
      queryClient.invalidateQueries(['media', mediaId])
      if (data.conversation_uuid) {
        queryClient.invalidateQueries(['media', data.conversation_uuid])
        queryClient.invalidateQueries(getMessagesCacheKey(data.conversation_uuid))
      }
    },
  })
}

export const useMediaSignedUrl = (mediaUuid) => {
  return useQuery(['mediaSignedUrl', mediaUuid], () => getMediaSignedUrl(mediaUuid), {
    enabled: !!mediaUuid,
    staleTime: 240000, // 4 minutes
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

// =============================================
// TENANT HOOKS
// =============================================

export const useTenants = () => {
  return useQuery(['tenants'], getAllTenants, {
    staleTime: 300000, // 5 minutes
    cacheTime: 1800000, // 30 minutes
  })
}

export const useTenant = (tenantId) => {
  return useQuery(['tenant', tenantId], () => getTenantById(tenantId), {
    enabled: !!tenantId,
    staleTime: 300000,
    cacheTime: 1800000,
  })
}

export const useCreateTenant = () => {
  const queryClient = useQueryClient()

  return useMutation(createTenant, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tenants'])
    },
  })
}

export const useUpdateTenant = () => {
  const queryClient = useQueryClient()

  return useMutation(({ tenantId, updateData }) => updateTenant(tenantId, updateData), {
    onSuccess: (data, { tenantId }) => {
      queryClient.invalidateQueries(['tenant', tenantId])
      queryClient.invalidateQueries(['tenants'])
    },
  })
}

export const useDeleteTenant = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteTenant, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tenants'])
    },
  })
}

// =============================================
// USER HOOKS
// =============================================

export const useUserProfile = () => {
  return useQuery(['userProfile'], getUserProfile, {
    staleTime: 300000, // 5 minutes
    cacheTime: 1800000, // 30 minutes
  })
}

// =============================================
// UTILITY HOOKS
// =============================================

export const useTestHrApiConnectivity = () => {
  return useMutation(testHrApiConnectivity, {
    onSuccess: (data) => {
      console.log('HR API connectivity test result:', data)
    },
    onError: (error) => {
      console.error('HR API connectivity test failed:', error)
    },
  })
}

export const useTestAuth = () => {
  return useQuery(['testAuth'], testAuth, {
    enabled: false, // Don't run automatically
    retry: false,
    staleTime: 0,
    cacheTime: 0,
  })
}
